import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import DocumentFilledIcon from '@atlaskit/icon/core/file';
import type { CardAction } from '@atlaskit/media-card/actions';
import Card from '@atlaskit/media-card/cardLoader';
import { MediaProvider } from '@atlaskit/media-client-react/media-provider';
import { FilmstripView, type ScrollEvent } from '@atlaskit/media-filmstrip/filmstrip-view';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { MediaClientConfig } from '../../types/attachment';
import type { FormFile } from '../../types/form/form-fields/field-values';
import { sortFilesByCreationDate } from '../../utils/attachment';
import { defaultImageCardDimensions } from './constants';

interface AttachmentFieldIssueAndRequestViewProps {
	actions?: CardAction[];
	files: FormFile[];
	mediaClientConfig: MediaClientConfig;
	isDragActive?: boolean;
}

type AnimateOffsetState = {
	animate: boolean;
	offset: number;
};

const styles = cssMap({
	dragPlaceholder: {
		color: token('color.text.accent.blue'),
		borderRadius: token('radius.small', '3px'),
		width: '150px',
		minHeight: '100px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const DragPlaceholder = () => (
	<Box
		xcss={styles.dragPlaceholder}
		backgroundColor="color.background.accent.blue.subtle"
		testId="field-input-attachment-view-filmstrip-drag-placeholder"
	>
		<DocumentFilledIcon color="currentColor" label="Document" spacing="spacious" />
	</Box>
);

export const AttachmentSharedFilmstrip = ({
	actions,
	files,
	mediaClientConfig,
	isDragActive,
}: AttachmentFieldIssueAndRequestViewProps): React.JSX.Element => {
	const [animateAndOffset, setAnimateAndOffset] = useState<AnimateOffsetState>({
		animate: false,
		offset: 0,
	});

	const { animate, offset } = animateAndOffset;
	const onScroll = (e: ScrollEvent) => {
		setAnimateAndOffset({
			animate: e.animate,
			offset: e.offset,
		});
	};

	return (
		<MediaProvider mediaClientConfig={mediaClientConfig}>
			<FilmstripView onScroll={onScroll} animate={animate} offset={offset}>
				{files
					.sort((a, b) => sortFilesByCreationDate(a, b))
					.map((file) => (
						<Box
							testId="field-input-attachment-view-filmstrip-card"
							key={file.identifier.id.toString()}
						>
							<Card
								key={file.identifier.id.toString()}
								mediaClientConfig={mediaClientConfig}
								identifier={file.identifier}
								actions={actions || []}
								shouldOpenMediaViewer
								shouldEnableDownloadButton
								dimensions={defaultImageCardDimensions}
							/>
						</Box>
					))}
			</FilmstripView>
			{isDragActive && (
				<FilmstripView offset={offset}>
					<DragPlaceholder />
				</FilmstripView>
			)}
		</MediaProvider>
	);
};
